import { useCallback, useEffect, useRef, useState } from "react";
import type { RouterOutputs } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, X } from "lucide-react";

type Question = RouterOutputs["food"]["sensory"]["questions"]["list"][number];
type QuestionType = Question["question_type"];
type TestType = "hedonic" | "comparative" | "general";

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  scale:         "Scale",
  short_text:    "Short Text",
  paragraph:     "Paragraph",
  single_choice: "Single Choice",
  multi_choice:  "Multi Choice",
  yes_no:        "Yes / No",
  sample_pick:   "Pick Sample",
  sample_rank:   "Rank Samples",
};

const GENERAL_ONLY_TYPES = new Set<string>(["sample_pick", "sample_rank"]);
const PER_SAMPLE_TYPES = new Set<string>(["scale", "short_text", "paragraph", "single_choice", "multi_choice", "yes_no"]);

function blankForm() {
  return {
    text:          "",
    question_type: "scale" as QuestionType,
    options:       ["", ""] as string[],
    min_value:     1,
    max_value:     10,
    min_label:     "",
    max_label:     "",
    required:      false,
  };
}

type QuestionFormData = {
  text:          string;
  question_type: QuestionType;
  options:       string[];
  min_value:     number;
  max_value:     number;
  min_label:     string;
  max_label:     string;
  required:      boolean;
};

export function QuestionDialog({
  open,
  onOpenChange,
  initial,
  isPending,
  testType,
  onSave,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial?: Question;
  isPending: boolean;
  testType: TestType;
  onSave: (data: {
    text:          string;
    question_type: QuestionType;
    options:       string[] | null;
    min_value:     number;
    max_value:     number;
    min_label:     string | null;
    max_label:     string | null;
    required:      boolean;
  }) => void;
}) {
  const [form, setForm] = useState<QuestionFormData>(blankForm);
  const isComparative = testType === "comparative";

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              text:          initial.text,
              question_type: initial.question_type,
              options:       initial.options && (initial.options as string[]).length > 0 ? (initial.options as string[]) : ["", ""],
              min_value:     initial.min_value,
              max_value:     initial.max_value,
              min_label:     initial.min_label ?? "",
              max_label:     initial.max_label ?? "",
              required:      initial.required,
            }
          : blankForm(),
      );
    }
  }, [open]);

  const set = <K extends keyof QuestionFormData>(key: K, value: QuestionFormData[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertAtCursor = useCallback((token: string) => {
    const el = textareaRef.current;
    if (!el) {
      set("text", form.text + token);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = form.text.slice(0, start) + token + form.text.slice(end);
    set("text", next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }, [form.text]);

  const isGeneral   = testType === "general";
  const usesOptions = form.question_type === "single_choice" || form.question_type === "multi_choice";
  const usesScale   = form.question_type === "scale";
  const isSamplePick = form.question_type === "sample_pick";
  const isSampleRank = form.question_type === "sample_rank";

  const availableTypes = Object.entries(QUESTION_TYPE_LABELS).filter(([key]) => {
    if (isGeneral) return true;
    return PER_SAMPLE_TYPES.has(key as QuestionType);
  }) as [QuestionType, string][];

  const hasValidOptions = !usesOptions || form.options.filter((o) => o.trim()).length > 1;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasValidOptions) return;
    onSave({
      text:          form.text.trim(),
      question_type: form.question_type,
      options:       usesOptions ? form.options.filter((o) => o.trim() !== "") : null,
      min_value:     form.min_value,
      max_value:     form.max_value,
      min_label:     usesScale && form.min_label.trim() ? form.min_label.trim() : null,
      max_label:     usesScale && form.max_label.trim() ? form.max_label.trim() : null,
      required:      form.required,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initial ? "Edit Question" : "Add Question"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex gap-3">
            <div className="space-y-1.5 flex-1">
              <Label>Question</Label>
              <div className="flex gap-2">
                <Textarea
                  ref={textareaRef}
                  value={form.text}
                  onChange={(e) => set("text", e.target.value)}
                  placeholder={
                    isGeneral
                      ? "e.g. Which sample is sweeter?"
                      : isComparative
                        ? "e.g. Is this sample sweeter than the reference?"
                        : "e.g. How much do you like the sweetness of this sample?"
                  }
                  rows={2}
                  required
                  className="flex-1"
                />
              </div>
              {(form.text.includes("{sample}") ||
                form.text.includes("{ref}")) && (
                <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 space-y-1">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                    Preview
                  </p>
                  <p className="text-sm">
                    {form.text
                      .replace(/\{sample\}/g, "306")
                      .replace(/\{ref\}/g, "REF")}
                  </p>
                </div>
              )}
            </div>

            {!isGeneral && (
              <div className="space-y-1.5">
                <div className="flex flex-col gap-1.5 shrink-0 pt-0.5">
                  <p className="text-xs">Insert</p>
                  <Button
                    type="button"
                    onClick={() => insertAtCursor("{sample}")}
                    className="cursor-pointer inline-flex items-center rounded-full border bg-muted/50 px-2.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors whitespace-nowrap"
                  >
                    + Sample
                  </Button>
                  {isComparative && (
                    <Button
                      type="button"
                      onClick={() => insertAtCursor("{ref}")}
                      className="cursor-pointer inline-flex items-center rounded-full border bg-muted/50 px-2.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors whitespace-nowrap"
                    >
                      + Reference
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select
              value={form.question_type}
              onValueChange={(v) => set("question_type", v as QuestionType)}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTypes.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(isSamplePick || isSampleRank) && (
            <p className="text-xs text-muted-foreground rounded-md border border-dashed bg-muted/30 px-3 py-2">
              {isSamplePick
                ? "The analyst will pick one sample from the session's sample list."
                : "The analyst will drag to rank all samples in order."}
            </p>
          )}

          {usesScale && (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Min value</Label>
                  <NumericInput
                    integer
                    value={form.min_value}
                    onValueChange={(v) => set("min_value", v ?? 1)}
                    className="h-8"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Max value</Label>
                  <NumericInput
                    integer
                    value={form.max_value}
                    onValueChange={(v) => set("max_value", v ?? 10)}
                    className="h-8"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Min label{" "}
                    <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    value={form.min_label}
                    onChange={(e) => set("min_label", e.target.value)}
                    placeholder="e.g. Not sweet"
                    className="h-8"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Max label{" "}
                    <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    value={form.max_label}
                    onChange={(e) => set("max_label", e.target.value)}
                    placeholder="e.g. Very sweet"
                    className="h-8"
                  />
                </div>
              </div>
            </div>
          )}

          {usesOptions && (
            <div className="space-y-2">
              <Label>Options</Label>
              <div className="space-y-2">
                {form.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={opt}
                      onChange={(e) => {
                        const next = [...form.options];
                        next[i] = e.target.value;
                        set("options", next);
                      }}
                      placeholder={`Option ${i + 1}`}
                      className="h-8"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          "options",
                          form.options.filter((_, j) => j !== i),
                        )
                      }
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      disabled={form.options.length <= 1}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => set("options", [...form.options, ""])}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add option
              </Button>
              {!hasValidOptions && (
                <p className="text-xs text-red-500">At least two options are required.</p>
              )}
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.required}
              onChange={(e) => set("required", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Required</span>
          </label>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !form.text.trim() || !hasValidOptions}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {initial ? "Save changes" : "Add question"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
